import { useEffect, useRef, useState, useCallback } from 'react';
import { wrap, type Remote } from 'comlink';
import { WORKER_CONFIG } from '../constants/worker';
import { sleep } from '../utils/workerHelpers';
import { sentryLogException } from '../services/sentryLogger';

export interface ComlinkWorkerHandle<T extends Record<string, any>> {
  worker: Remote<T> | null;
  /**
   * Terminate the current worker (if any) and create a fresh one.
   * Used when a worker RPC hangs (e.g. tf.loadGraphModel on a blocked URL)
   * so later recovery attempts are not queued behind the stuck call.
   */
  recreateWorker: () => Promise<Remote<T> | null>;
}

type CreatedWorker<T> = {
  instance: Worker;
  wrapped: Remote<T>;
  verified: boolean;
};

/**
 * React hook for using Comlink web workers
 * Replaces @shopify/react-web-worker with React 19 support
 * 
 * Note: Comlink proxies cannot be stored in React state (they're not serializable).
 * We store the proxy in a ref and use a boolean state flag to trigger re-renders.
 */
export function useComlinkWorker<T extends Record<string, any>>(
  workerImport: () => Promise<{ default: new () => Worker }>
): ComlinkWorkerHandle<T> {
  const workerProxyRef = useRef<Remote<T> | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const recreateInFlightRef = useRef<Promise<Remote<T> | null> | null>(null);
  const recreateGenerationRef = useRef(0);
  const [workerReady, setWorkerReady] = useState(false);
  const workerImportRef = useRef(workerImport);
  workerImportRef.current = workerImport;

  /**
   * Verify worker API is accessible
   * @param wrapped - The Comlink-wrapped worker proxy
   * @param requiredMethod - Optional method name that must be accessible for verification to pass
   */
  const verifyWorkerAPI = useCallback(async (
    wrapped: Remote<T>,
    requiredMethod?: string
  ): Promise<{ verified: boolean; methods: string[] }> => {
    for (let attempt = 0; attempt < WORKER_CONFIG.MAX_VERIFICATION_ATTEMPTS; attempt++) {
      try {
        const getMethods = (wrapped as any).getApiMethods;
        if (!getMethods) {
          await sleep(WORKER_CONFIG.VERIFICATION_RETRY_DELAY);
          continue;
        }

        const methods = await getMethods() as string[];

        // No required method check - verification complete
        if (!requiredMethod) {
          return { verified: true, methods };
        }

        // Verify required method exists in API and is accessible
        const isMethodInApi = methods.includes(requiredMethod);
        const isMethodAccessible = (wrapped as any)[requiredMethod] != null;

        if (isMethodInApi && isMethodAccessible) {
          return { verified: true, methods };
        }

        // Method not ready, retry
        await sleep(WORKER_CONFIG.VERIFICATION_RETRY_DELAY);
      } catch {
        // Retry on error (worker might not be ready yet)
        if (attempt < WORKER_CONFIG.MAX_VERIFICATION_ATTEMPTS - 1) {
          await sleep(WORKER_CONFIG.VERIFICATION_RETRY_DELAY);
        }
      }
    }

    return { verified: false, methods: [] };
  }, []);

  /**
   * Create a Worker + Comlink proxy without touching module refs.
   * Callers assign refs only after generation/mount checks succeed so a stale
   * recreate cannot terminate a newer worker.
   */
  const createAndVerifyWorker = useCallback(async (): Promise<CreatedWorker<T> | null> => {
    const workerModule = await workerImportRef.current();
    const WorkerConstructor = workerModule.default;

    if (!WorkerConstructor) {
      throw new Error('Worker module does not export a default Worker constructor');
    }

    const instance = new WorkerConstructor();
    const wrapped = wrap<T>(instance);

    await sleep(WORKER_CONFIG.INITIALIZATION_DELAY);

    const { verified, methods } = await verifyWorkerAPI(wrapped, 'loadModel');

    if (verified) {
      console.log('Worker initialized successfully', { methods });
    } else {
      console.warn('Worker initialized but API verification failed', { methods });
    }

    return { instance, wrapped, verified };
  }, [verifyWorkerAPI]);

  const recreateWorker = useCallback(async (): Promise<Remote<T> | null> => {
    if (recreateInFlightRef.current) {
      return recreateInFlightRef.current;
    }

    recreateInFlightRef.current = (async (): Promise<Remote<T> | null> => {
      const generation = ++recreateGenerationRef.current;
      try {
        workerRef.current?.terminate();
        workerRef.current = null;
        workerProxyRef.current = null;
        setWorkerReady(false);

        const created = await createAndVerifyWorker();

        // A newer recreate started while we were creating — discard this worker
        if (generation !== recreateGenerationRef.current) {
          created?.instance.terminate();
          return null;
        }

        if (!created?.verified) {
          created?.instance.terminate();
          setWorkerReady(false);
          return null;
        }

        workerRef.current = created.instance;
        workerProxyRef.current = created.wrapped;
        setWorkerReady(true);
        return created.wrapped;
      } catch (error) {
        console.error('Failed to recreate worker:', error);
        sentryLogException({
          error,
          tags: {
            component: 'useComlinkWorker',
            category: 'workerRecreation',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
        if (generation === recreateGenerationRef.current) {
          workerProxyRef.current = null;
          workerRef.current = null;
          setWorkerReady(false);
        }
        return null;
      }
    })();

    try {
      return await recreateInFlightRef.current;
    } finally {
      recreateInFlightRef.current = null;
    }
  }, [createAndVerifyWorker]);

  useEffect(() => {
    let isMounted = true;

    const initWorker = async () => {
      try {
        const created = await createAndVerifyWorker();
        if (!isMounted) {
          created?.instance.terminate();
          return;
        }

        if (created?.verified) {
          workerRef.current = created.instance;
          workerProxyRef.current = created.wrapped;
          setWorkerReady(true);
        } else {
          created?.instance.terminate();
          workerProxyRef.current = null;
          workerRef.current = null;
          setWorkerReady(false);
        }
      } catch (error) {
        console.error('Failed to create worker:', error);
        sentryLogException({
          error,
          tags: {
            component: 'useComlinkWorker',
            category: 'workerCreation',
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        });
        if (isMounted) {
          workerProxyRef.current = null;
          workerRef.current = null;
          setWorkerReady(false);
        }
      }
    };

    initWorker();

    return () => {
      isMounted = false;
      workerRef.current?.terminate();
      workerRef.current = null;
      workerProxyRef.current = null;
    };
  }, [createAndVerifyWorker]);

  return {
    worker: workerReady ? workerProxyRef.current : null,
    recreateWorker,
  };
}

/**
 * Creates a worker factory function (for API compatibility with @shopify/react-web-worker)
 */
export function createComlinkWorkerFactory<T extends Record<string, any>>(
  moduleLoader: () => Promise<T>
): () => Promise<{ default: new () => Worker }> {
  try {
    if(moduleLoader.toString().includes('tensorflow')) {
      return () => import('../workers/tensorflow.worker.ts?worker');
    } else {
      throw new Error(`Unknown module path: ${moduleLoader}`);
    }
  } catch (error) {
    throw new Error(`Unknown module path: ${moduleLoader}`);
  }
}
